import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, defer } from 'rxjs';
import {
  exhaustMap,
  switchMap,
  map,
  catchError,
  distinctUntilChanged
} from 'rxjs/operators';
import {
  UserPreferencesActionTypes,
  LoadUserPreferencesSuccess,
  LoadUserPreferencesFail,
  SaveUserPreference,
  SaveUserPreferenceSuccess,
  SaveUserPreferenceFail,
  DeleteUserPreference,
  DeleteUserPreferenceSuccess,
  DeleteUserPreferenceFail
} from './actions';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { UserPreference } from '../../types/user-preferences.types';
import { PreferencesFacade } from '../../facades/preferences.facade';
import { SetFlexColumn } from '../calendar/actions';
import { SetMargTaxRate } from '../series-details/actions';
import { flexColumnDefaultOptions } from '../../grid/types/column.types';

@Injectable()
export class UserPreferencesEffects {
  constructor(
    private actions$: Actions,
    private userPrefService: UserPreferencesService,
    private preferencesFacade: PreferencesFacade
  ) {}

  loadUserPreferences$: Observable<
    LoadUserPreferencesSuccess | LoadUserPreferencesFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActionTypes.Load),
      exhaustMap(() =>
        this.userPrefService.load().pipe(
          map((userPrefs: UserPreference[]) => {
            return new LoadUserPreferencesSuccess(userPrefs);
          }),
          catchError((err: Error) =>
            of(new LoadUserPreferencesFail(err && err.message))
          )
        )
      )
    )
  );

  saveUserPreferences$: Observable<
    SaveUserPreferenceSuccess | SaveUserPreferenceFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActionTypes.Save),
      switchMap((action: SaveUserPreference) =>
        this.userPrefService.save(action.payload).pipe(
          map((pref) => new SaveUserPreferenceSuccess(pref as any)),
          catchError((err: Error) =>
            of(new SaveUserPreferenceFail(err && err.message))
          )
        )
      )
    )
  );

  deleteUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPreferencesActionTypes.Delete),
      switchMap((action: DeleteUserPreference) =>
        this.userPrefService.delete(action.payload).pipe(
          map(
            (userPreferences) =>
              new DeleteUserPreferenceSuccess(userPreferences)
          ),
          catchError((err: Error) =>
            of(new DeleteUserPreferenceFail(err && err.message))
          )
        )
      )
    )
  );

  loadUserPreferencesWhenChanged$ = createEffect(() =>
    defer(
      (): Observable<SetMargTaxRate | SetFlexColumn> =>
        this.preferencesFacade.preferences$.pipe(
          distinctUntilChanged(),
          switchMap((preferences) => {
            const defaultOption = flexColumnDefaultOptions.find(
              (option) => option.label === preferences.defaultFlexColumn
            );

            return [
              new SetMargTaxRate({
                'mtr-pricing': preferences.defaultMarginalTaxRate
              }),
              new SetFlexColumn(defaultOption ? defaultOption.name : null)
            ];
          })
        )
    )
  );
}
